import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { EditIcon, PersonAddIcon } from 'components/Icons'
import TableHeadCell from 'components/TableHeadCell'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { AiOutlineDelete } from 'react-icons/ai'
import { getCourseSessionsEnrollmentType, getAllStaff } from 'store/lookup'
import { useEffect, useState } from 'react'
import Confirm from 'components/Dialog'
import { destroy } from 'store/scheduling/course-session-enrollments'
import { destroyCourseSessionEnrollment } from 'store/scheduling/course-session'
import { search } from 'store/scheduling/course-session-enrollments'
import dayjs from 'dayjs'
import { useSession } from 'components/Providers'

const heads = ['Name', 'Role', 'Start Date', 'End Date', 'Sub', 'Actions']

const checkNoLongerEmployed = (item, academicYear) =>
  academicYear && item?.schoolEnrollment?.campusEnrollments?.some(ce => {
    if (!ce.endDate) return false
    const end = dayjs(ce.endDate)
    return !end.isBefore(dayjs(academicYear.startDate), 'day') &&
           !end.isAfter(dayjs(academicYear.endDate), 'day')
  })

function TeacherRow({ item, courseSessionId, basePath, theme, onDelete, deletingId, academicYear }) {
  const isDeleting = deletingId === item?.id
  const iconColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.56)'
  const isNoLongerEmployed = checkNoLongerEmployed(item, academicYear)

  return (
    <TableRow>
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Link component={NavLink} underline="hover" color="inherit" to={`${basePath}${courseSessionId}/teacher/${item?.id}`}>
            {item?.schoolEnrollment?.person?.lastName}, {item?.schoolEnrollment?.person?.firstName}
          </Link>
          {isNoLongerEmployed && (
            <Tooltip arrow title="No longer employed at the school">
              <Chip label="Inactive" size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      <TableCell>{item?.courseSessionEnrollmentType?.name}</TableCell>
      <TableCell>{dayjs(item?.startDate).format('MM/DD/YYYY')}</TableCell>
      <TableCell>{item?.endDate ? dayjs(item?.endDate).format('MM/DD/YYYY') : '—'}</TableCell>
      <TableCell>
        <Checkbox color="primary" size="small" checked={item?.substitute} />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isDeleting ? (
            <CircularProgress size={18} sx={{ mr: '5px' }} />
          ) : (
            <AiOutlineDelete
              fontSize={24}
              onClick={() => onDelete(item?.id)}
              style={{ cursor: 'pointer', marginRight: '5px', color: iconColor }}
            />
          )}
          <Link component={NavLink} underline="hover" color="inherit" to={`${basePath}${courseSessionId}/teacher/${item?.id}`}>
            <EditIcon fontSize="medium" sx={{ color: iconColor, display: 'block' }} />
          </Link>
        </Box>
      </TableCell>
    </TableRow>
  )
}

export default function SchedulingCourseSessionsFormAssignTeacher() {
  const dispatch = useDispatch()
  const [confirm, setConfirm] = useState({ status: false, id: null })
  const [deletingId, setDeletingId] = useState(null)
  const { t } = useTranslation()
  const theme = useSelector(state => state.session.appearance)
  const navigate = useNavigate()
  const basePath = `/scheduling/course-sessions/`
  const { courseSessionId } = useParams()
  const courseSessionsEnrollment = useSelector(state => state.scheduling?.courseSessionsEnrollments?.list)
  const loading = useSelector(state => state.scheduling?.courseSessionsEnrollments?.recordLoading)
  const staffRole = useSelector(state => state.lookup?.courseSessionsEnrollmentType)
  const staff = useSelector(state => state.lookup.allStaff?.rows)
  const { academicYear } = useSession();
  

  useEffect(() => {
    staff || dispatch(getAllStaff({ limit: 1000 }))
    staffRole || dispatch(getCourseSessionsEnrollmentType())
  }, [])

  useEffect(() => {
    if (+courseSessionId) {
      dispatch(search({ courseSessionId: +courseSessionId, type: 'staff' }))
    }
  }, [courseSessionId])

  const onDestroy = async () => {
    const id = confirm.id
    setConfirm({ status: false, id: null })
    if (id && +courseSessionId) {
      setDeletingId(id)
      await dispatch(destroy({ id, courseSessionId: +courseSessionId }))
      dispatch(destroyCourseSessionEnrollment(id))
      setDeletingId(null)
    }
  }

  if (!(staff && staffRole && courseSessionsEnrollment)) return null

  const activeTeachers = courseSessionsEnrollment.filter(item => item?.id && !item?.endDate && !checkNoLongerEmployed(item, academicYear))
  const formerTeachers = courseSessionsEnrollment.filter(item => item?.id && (item?.endDate || checkNoLongerEmployed(item, academicYear)))
  const leadTeacherCount = activeTeachers.filter(item => item?.courseSessionEnrollmentType?.id === 'lead-teacher').length
  const hasLeadTeacher = leadTeacherCount > 0

  const rowProps = { courseSessionId, basePath, theme, onDelete: id => setConfirm({ status: true, id }), deletingId, academicYear }

  const tableStyles = {
    border: '1px solid',
    borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
    my: 2,
  }

  if (loading) return <LinearProgress sx={{ width: '100%', mt: 2 }} color="primary" />

  return (
    <Box sx={{ pt: 3, pl: 1 }}>
      <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{t('Assign Teacher(s)')}</Typography>
        <Button
          onClick={() => navigate(`${basePath}${courseSessionId}/teacher`)}
          size="small"
          color="primary"
          variant="outlined"
          startIcon={<PersonAddIcon />}
        >
          {t('Add')}
        </Button>
      </Stack>

      {activeTeachers.length > 0 && !hasLeadTeacher && (
        <Alert severity="warning" sx={{ my: 1 }}>
          {t('No lead teacher assigned. Please assign a lead teacher to this session.')}
        </Alert>
      )}
      {leadTeacherCount >= 2 && (
        <Alert severity="error" sx={{ my: 1 }}>
          {t('Multiple lead teachers assigned. A session should only have one lead teacher.')}
        </Alert>
      )}

      <Table sx={tableStyles}>
        <TableHead>
          <TableRow>
            {heads.map((item, index) => (
              <TableHeadCell key={index} sx={{ fontWeight: '500 !important' }}>
                {t(item)}
              </TableHeadCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {activeTeachers.map((item, index) => (
            <TeacherRow key={index} item={item} {...rowProps} />
          ))}
        </TableBody>
      </Table>

      {formerTeachers.length > 0 && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            borderRadius: 1,
            '&:before': { display: 'none' },
            mt: 1,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" fontWeight={600}>{t('Former Teachers')}</Typography>
              <Chip label={formerTeachers.length} size="small" color="default" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {heads.map((item, index) => (
                    <TableHeadCell key={index} sx={{ fontWeight: '500 !important' }}>
                      {t(item)}
                    </TableHeadCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {formerTeachers.map((item, index) => (
                  <TeacherRow key={index} item={item} {...rowProps} />
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}

      <Confirm open={confirm.status} onClose={() => setConfirm({ status: false, id: null })} onConfirm={onDestroy} />
    </Box>
  )
}
